import { TeamResource } from 'dy-frontend-http-repository/lib/modules/Team/resources';
import { useStore } from 'effector-react';
import React, { useEffect, useState } from 'react';

import { usePageTitle } from '@app/hooks';
import { Callout, Intent, Spinner, Tab, Tabs } from '@blueprintjs/core';
import FixedWidthPageContainer from '@components/FixedWidthPageContainer';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import { $authorizedUser } from '@containers/store/states';
import { LocalStorage } from '@data/enums';

import MonthlyOccupancy from './components/MonthlyOccupancy';
import QuarterlyOccupancy from './components/QuarterlyOccupancy';
import { RoleSelectionProps } from './components/RoleFilter';
import WeeklyOccupancy from './components/WeeklyOccupancy';
import { fetchActiveTeams } from './data/api';
import {
    AccountRoles,
    TeamHealthView,
    getAccountRoles,
    parseStoredRoles,
    parseTeamHealthView,
} from './utils/teamHealth';

const readStoredView = (): TeamHealthView => {
    try {
        return parseTeamHealthView(localStorage.getItem(LocalStorage.TEAM_HEALTH_VIEW));
    } catch {
        return parseTeamHealthView(null);
    }
};

const storeView = (view: TeamHealthView) => {
    try {
        localStorage.setItem(LocalStorage.TEAM_HEALTH_VIEW, view);
    } catch {
        // Storage unavailable: the view simply is not remembered
    }
};

// Roles are remembered per signed-in account, in this browser; without an account nothing is read or written
const getRolesStorageKey = (userId: string) => `${LocalStorage.TEAM_HEALTH_ROLES}/${userId}`;

const readStoredRoles = (userId: string | null): string[] => {
    if (userId === null) {
        return [];
    }

    try {
        return parseStoredRoles(localStorage.getItem(getRolesStorageKey(userId)));
    } catch {
        return [];
    }
};

const storeRoles = (userId: string | null, roles: string[]) => {
    if (userId === null) {
        return;
    }

    try {
        if (roles.length === 0) {
            localStorage.removeItem(getRolesStorageKey(userId));
        } else {
            localStorage.setItem(getRolesStorageKey(userId), JSON.stringify(roles));
        }
    } catch {
        // Storage unavailable: the roles simply are not remembered
    }
};

const TeamHealth: React.FC = () => {
    usePageTitle('Occupancy');

    const me = useStore($authorizedUser);
    const userId = me?.user.id ?? null;

    const [view, setView] = useState<TeamHealthView>(readStoredView);
    const [teams, setTeams] = useState<TeamResource[] | null>(null);
    const [isTeamsError, setIsTeamsError] = useState(false);

    // Roles are held with their account, so after an account change the previous account's are never shown or saved
    const [heldRoles, setHeldRoles] = useState<AccountRoles>(() => ({ userId, roles: readStoredRoles(userId) }));
    const roleSelection: RoleSelectionProps = {
        selectedRoles: getAccountRoles(heldRoles, userId, readStoredRoles),
        onSelectedRolesChange: (roles) => {
            setHeldRoles({ userId, roles });
            storeRoles(userId, roles);
        },
    };

    useEffect(() => {
        let isCancelled = false;
        fetchActiveTeams()
            .then((items) => !isCancelled && setTeams(items))
            .catch((e) => {
                console.error(e);
                if (!isCancelled) {
                    setIsTeamsError(true);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, []);

    const renderContent = () => {
        if (isTeamsError) {
            return <Callout intent={Intent.DANGER}>Teams could not be loaded.</Callout>;
        }

        if (!teams) {
            return (
                <Flex justify="center">
                    <Spinner />
                </Flex>
            );
        }

        if (view === 'quarterly') {
            return <QuarterlyOccupancy teams={teams} {...roleSelection} />;
        }

        return view === 'monthly' ?
                <MonthlyOccupancy teams={teams} {...roleSelection} />
            :   <WeeklyOccupancy teams={teams} {...roleSelection} />;
    };

    return (
        <FixedWidthPageContainer>
            <Heading type="h3" className="mb-2">
                Occupancy
            </Heading>
            <Tabs
                id="team-health-view"
                className="mb-2"
                large
                selectedTabId={view}
                onChange={(tabId) => {
                    const nextView = parseTeamHealthView(String(tabId));
                    setView(nextView);
                    storeView(nextView);
                }}
            >
                <Tab id="monthly" title="Monthly" />
                <Tab id="weekly" title="Weekly" />
                <Tab id="quarterly" title="Quarterly" />
            </Tabs>
            {renderContent()}
        </FixedWidthPageContainer>
    );
};

export default TeamHealth;
